import type {DomainRealtimeHandler, ResourceBroadcast} from "./realtime.types";
import type {QueryClient} from "@tanstack/react-query";

import {EventSource} from "eventsource";

export interface RealtimeConnectOptions {
  onError?: (event: Event) => void;
}

/**
 * Singleton class to manage the single SSE connection.
 * It prevents browser connection limits by multiplexing all events
 * through a single stream.
 */
export class RealtimeEventRouter {
  private queryClient: QueryClient;
  private eventSource: EventSource | null = null;
  private handlers: Record<string, DomainRealtimeHandler>;

  constructor(queryClient: QueryClient, handlers: Record<string, DomainRealtimeHandler> = {}) {
    this.queryClient = queryClient;
    this.handlers = handlers;
  }

  /**
   * Start the SSE connection.
   */
  public connect(options: RealtimeConnectOptions = {}) {
    if (this.eventSource) return;

    this.eventSource = new EventSource("/api/realtime");

    this.eventSource.addEventListener("message", (event) => {
      try {
        const broadcast = JSON.parse(event.data) as ResourceBroadcast;

        this.routeEvent(broadcast);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to parse SSE event", e);
      }
    });

    this.eventSource.addEventListener("error", (event) => {
      // EventSource natively handles reconnection on network errors.
      // The shell-level caller can revalidate the auth session because browsers
      // do not reliably expose the HTTP status for SSE failures.
      options.onError?.(event);
      // eslint-disable-next-line no-console
      console.error("SSE connection error", event);
    });
  }

  /**
   * Stop the SSE connection.
   */
  public disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Route the incoming event to the correct TanStack Query invalidation.
   */
  private routeEvent(event: ResourceBroadcast) {
    const handler = this.handlers[event.resource];

    if (handler) {
      handler.handleEvent(event, this.queryClient);
    }
  }
}
